import { FuelProductType } from "~/types";
import { PelletsChartData, PelletsChartPoint } from "~/types/Charts";
import { CityPriceModel, PricePayload } from "~/types/Location";
import { Offer } from "~/types/Offer";
import { PelletProductPriceState, PelletsMetrics } from "~/types/Pellets";

function getBruttoPrice(offer: Offer, product: FuelProductType): number {
  return product === "pellets"
    ? offer.pricing.ton?.brutto || 0
    : offer.pricing._100L?.brutto || 0;
}

export function getMinBruttoPrice(
  offers: Offer[] | undefined | null,
  product: FuelProductType,
): number {
  if (!offers?.length) return 0;
  return Math.min(...offers.map((offer) => getBruttoPrice(offer, product)));
}

export function getMaxBruttoPrice(
  offers: Offer[] | undefined | null,
  product: FuelProductType,
): number {
  if (!offers?.length) return 0;
  return Math.max(...offers.map((offer) => getBruttoPrice(offer, product)));
}

export function calculatePelletsMetrics(
  data: PelletsChartPoint[],
): PelletsMetrics {
  const validValues = data.filter((point) => point.y > 0);
  const currentPrice =
    validValues.length > 0 ? validValues[validValues.length - 1].y : 0;
  const last7Days = validValues.slice(-7);
  const sevenDayAverage =
    last7Days.length > 0
      ? last7Days.reduce((sum, point) => sum + point.y, 0) / last7Days.length
      : 0;

  return {
    currentPrice,
    sevenDayAverage,
    isPriceHigher: currentPrice > sevenDayAverage,
  };
}

export function calculatePelletsChartMetrics(chartData: PelletsChartData) {
  return {
    loose: calculatePelletsMetrics(chartData.loose.data),
    pallet: calculatePelletsMetrics(chartData.pallet.data),
  };
}

export function createCityPriceModel(
  city: string,
  data: any,
  dates: { actual: string; previous: string },
): CityPriceModel {
  return {
    name: city,
    zipcode: data[city].zipcode,
    seoName: data[city].seoName,
    price: {
      actual: data[city][dates.actual],
      previous: data[city][dates.previous],
      maxPrice24Month: data[city].max,
      minPrice24Month: data[city].min,
      change: data[city][dates.actual] - data[city][dates.previous],
    },
  };
}

export function isPriceInvalid(price: number): boolean {
  if (typeof price === "number") {
    return price === 0 || isNaN(price);
  }
  if (typeof price === "string") {
    const parsed = Number(price);
    return isNaN(parsed) || parsed === 0;
  }
  return true;
}

export function pricePayloadToProductPrice(
  data: PricePayload,
): PelletProductPriceState {
  return {
    currentPrice: {
      value: data.currentPrice.value,
      changedAtUnix: data.currentPrice.modifiedAt,
    },
    lastPrice: {
      value: data.lastPrice.value,
      changedAtUnix: data.lastPrice.modifiedAt,
    },
    relativeChange: data.relativeChange.toFixed(2),
  };
}
